import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CustomInput from "../../FormFields/CustomInput/CustomInput";
import CustomDate from "../../CustomForm/CustomDate";
import CustomCheckbox from "../../CustomForm/CustomCheckbox";
import CustomAutoComplete from "../../FormFields/CustomSelect/CustomAutoComplete";
import { QK } from "../../../base/qk";
import { useQuery } from "react-query";
import { searchCandidates } from "../../../api/services/candidate";

const usePersonSearch = (searchTerm) =>
  useQuery(
    [QK.searchCareers, searchTerm],
    () => searchCandidates(searchTerm),
    { enabled: true, keepPreviousData: true }
  );

const RiddorInfo = ({ formData, handleChange }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const [reporterSearch, setReporterSearch] = useState("");

  const managerQuery = usePersonSearch(managerSearch);
  const reporterQuery = usePersonSearch(reporterSearch);

  const formatOptions = (data) =>
    (data || []).map((person) => ({
      id: person.id,
      name: `${person.first_name} ${person.last_name}`.trim(),
    }));

  const updateRiddorData = (updates) => {
    handleChange({
      target: {
        name: "riddor",
        value: { ...formData.riddor, ...updates },
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateRiddorData({ [name]: type === "checkbox" ? checked : value });
  };

  const handlePersonSelect = (field) => (selectedId) => {
    updateRiddorData({ [field]: selectedId });
  };

  const handleSearchInput = (setter) => (value) => {
    setter(value);
  };

  const isNotRequired = formData.riddor?.not_required || false;

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        boxShadow: "none",
        border: "1px solid #EAECF0",
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#475467" }} />}
        sx={{
          backgroundColor: "#FAFBFC",
          minHeight: "56px",
          "&.Mui-expanded": { minHeight: "56px" },
          "& .MuiAccordionSummary-content": {
            margin: "12px 0",
            "&.Mui-expanded": { margin: "12px 0" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReportProblemIcon
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1A1A2E"
                fontSize="0.9rem"
              >
                RIDDOR
              </Typography>
              <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
                Reportable injuries, diseases and dangerous occurrences
              </Typography>
            </Box>
          </Box>

          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              name="not_required"
              label="Not required"
              isInitiallyChecked={isNotRequired}
              onClick={(checked) =>
                updateRiddorData({ not_required: checked })
              }
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3, bgcolor: "#FFFFFF" }}>
        {isNotRequired && (
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              bgcolor: "#F9FAFB",
              border: "1px solid #EAECF0",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="#6B7280"
              fontSize="0.8rem"
              fontWeight={500}
            >
              RIDDOR notification marked as <strong>Not Required</strong> for
              this incident.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            opacity: isNotRequired ? 0.5 : 1,
            pointerEvents: isNotRequired ? "none" : "auto",
          }}
        >
          <CustomInput
            name="reference_number"
            label="RIDDOR Reference Number"
            value={formData.riddor?.reference_number || ""}
            onChange={handleInputChange}
          />

          <CustomAutoComplete
            options={formatOptions(managerQuery.data)}
            isLoading={managerQuery.isLoading}
            value={formData?.riddor?.manager_informed || null}
            onChange={handlePersonSelect("manager_informed")}
            inputValue={managerSearch}
            onInputChange={handleSearchInput(setManagerSearch)}
            label="Name of Manager Informed"
            placeholder="Search for a manager..."
          />

          <CustomInput
            name="other_manager"
            label="Other Manager Informed"
            value={formData.riddor?.other_manager || ""}
            onChange={handleInputChange}
          />

          <CustomAutoComplete
            options={formatOptions(reporterQuery.data)}
            isLoading={reporterQuery.isLoading}
            value={formData?.riddor?.reported_by || null}
            onChange={handlePersonSelect("reported_by")}
            inputValue={reporterSearch}
            onInputChange={handleSearchInput(setReporterSearch)}
            label="Reported By"
            placeholder="Search for a reporter..."
          />

          <CustomInput
            name="other_reporter"
            label="Other Reporter"
            value={formData.riddor?.other_reporter || ""}
            onChange={handleInputChange}
          />

          <CustomDate
            name="date_informed"
            label="Date Informed"
            value={formData.riddor?.date_informed || ""}
            onChange={handleInputChange}
          />

          <CustomDate
            name="sent_on"
            label="Sent On"
            value={formData.riddor?.sent_on || ""}
            onChange={handleInputChange}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RiddorInfo;